/**
 * CustomerHistoryModal — presents the full interaction history for a customer.
 *
 * Mounted at the AppShell level (sibling of ReviewThreadModal), driven by
 * `useQueueStore.customerHistoryModalEmail` / `customerHistoryModalId`.
 *
 * ## Two hosts, one page (Task 4094)
 *
 * **Desktop (`IS_TAURI`)** — unchanged. Renders nothing in the main DOM; the
 * "modal" IS a new OS window. When a customer becomes non-null it:
 *   1. Derives a stable Tauri window label from the email (prefix `ch-`).
 *   2. Closes any existing window with that label (prevents stale data) and
 *      creates a new `WebviewWindow` loading `index.html?ch=1` with the
 *      customer email, id, name, consumerId and env as query params — both
 *      handled inside `openPopout`.
 *   3. Immediately calls `closeCustomerHistoryModal()` so the store is reset,
 *      allowing the same customer to be re-opened later.
 *
 * **Browser** — renders `CustomerHistoryPage` as an in-page `PopoutPanel`
 * overlay inside the live app. No OS window, and deliberately no
 * `window.open`: see the rationale in `PopoutPanel.tsx`. The store state
 * therefore has to *persist* while the panel is open (the desktop path resets
 * it immediately because the OS window has already taken ownership of the
 * data), and is cleared when the operator dismisses the panel.
 *
 * Re-opening the same customer twice presents fresh data in both hosts: on
 * desktop by tearing the window down and rebuilding it, in the browser by
 * keying the panel on `customerHistoryModalOpenCount`, which the store bumps
 * on every open — so React remounts the subtree even when every other prop is
 * identical.
 */

import { useEffect, type ReactNode } from 'react'
import { openPopout } from '@/lib/popout'
import { IS_TAURI } from '@/lib/platform'
import { useQueueStore } from '../../store'
import { useOperatorStore } from '@/shared/stores/operator-store'
import { useEnvironmentStore } from '@/shared/stores/environment-store'
import { toast } from '@/shared/hooks/use-toast'
import { PopoutPanel } from '../PopoutPanel'
import { CustomerHistoryPage } from '@/pages/CustomerHistoryPage'

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Derive a stable, label-safe Tauri window identifier for a customer.
 * Tauri window labels must match `[a-zA-Z0-9_-/]+`, max 64 chars.
 * We prefix with `ch-` so the capability's `"ch-*"` glob matches.
 */
function customerLabel(email: string | null, customerId: string | null): string {
  const key = email ?? customerId ?? 'unknown'
  return `ch-${key.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`
}

// ── Desktop: a real OS pop-out window ────────────────────────────────────────

function CustomerHistoryPopoutWindow(): ReactNode {
  const email      = useQueueStore((s) => s.customerHistoryModalEmail)
  const customerId = useQueueStore((s) => s.customerHistoryModalId)
  const name       = useQueueStore((s) => s.customerHistoryModalName)

  useEffect(() => {
    if (!email && !customerId) return

    const openHistoryWindow = async (): Promise<void> => {
      const consumerId = useOperatorStore.getState().selected?.consumer_id ?? ''
      const env        = useEnvironmentStore.getState().active

      const label = customerLabel(email, customerId)

      const params = new URLSearchParams()
      if (email)      params.set('customerEmail', email)
      if (customerId) params.set('customerId', customerId)
      if (name)       params.set('customerName', name)
      if (consumerId) params.set('consumerId', consumerId)
      params.set('env', env)

      const windowTitle = name ?? email ?? customerId ?? 'Customer History'

      // Closing any existing window with this label, so the new one always
      // fetches the latest data, is handled inside openPopout (Task 4093).
      const result = await openPopout({
        label,
        url:       `index.html?ch=1&${params.toString()}`,
        title:     windowTitle,
        width:     900,
        height:    1040,
        minWidth:  600,
        minHeight: 600,
      })
      if (!result.ok) {
        // `unsupported` is unreachable here — this component only mounts under
        // IS_TAURI, where openPopout takes the WebviewWindow path — so any
        // failure is a real window-creation failure worth surfacing.
        console.error('[CustomerHistoryModal] Failed to create window:', result.error)
        toast({
          title: 'Could not open customer history',
          description: 'The window failed to open. Try again.',
          variant: 'danger',
        })
      }

      // Reset store immediately so the same customer can be re-opened.
      useQueueStore.getState().closeCustomerHistoryModal()
    }

    void openHistoryWindow()
  }, [email, customerId, name])

  return null
}

// ── Browser: an in-page panel over the live app ──────────────────────────────

function CustomerHistoryInPagePanel(): ReactNode {
  const email      = useQueueStore((s) => s.customerHistoryModalEmail)
  const customerId = useQueueStore((s) => s.customerHistoryModalId)
  const name       = useQueueStore((s) => s.customerHistoryModalName)
  const openCount  = useQueueStore((s) => s.customerHistoryModalOpenCount)
  const close      = useQueueStore((s) => s.closeCustomerHistoryModal)

  const open = Boolean(email || customerId)
  if (!open) return null

  const title = name ?? email ?? customerId ?? 'Customer History'

  return (
    <PopoutPanel
      title={`Customer history — ${title}`}
      description="Full interaction history for this customer. Press Escape to close."
      open
      onClose={close}
    >
      {/*
        `key` forces a remount whenever the operator opens the panel again,
        including for the identical customer. That is what makes "re-open and
        see fresh data" true here — the desktop path gets it by destroying and
        recreating the OS window instead.
      */}
      <CustomerHistoryPage
        key={`${email ?? ''}|${customerId ?? ''}|${openCount}`}
        customerEmail={email}
        customerId={customerId}
        customerName={name}
        embedded
      />
    </PopoutPanel>
  )
}

// ── Component ────────────────────────────────────────────────────────────────

export function CustomerHistoryModal(): ReactNode {
  // A build-time constant, so the browser bundle folds this to the panel and
  // the desktop bundle to the window — neither carries the other's branch.
  return IS_TAURI ? <CustomerHistoryPopoutWindow /> : <CustomerHistoryInPagePanel />
}

export default CustomerHistoryModal
